import { useState, useEffect } from 'react';
import { AdminSidebar } from './AdminDashboard';
import api from '../lib/api';
import { Plus, PencilSimple, Trash, X } from '@phosphor-icons/react';

const TYPES = ['BERLINE', 'VIP', 'MINIBUS'];

export default function AdminVehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'BERLINE', seats: 4, pricePerKm: 1.5, description: '', imageUrl: '', isActive: true });

  const fetchVehicles = () => {
    api.get('/vehicles/all').then(r => setVehicles(r.data)).catch(() => {});
  };

  useEffect(() => { fetchVehicles(); }, []);

  const handleSave = async () => {
    try {
      if (editingId) {
        await api.put(`/vehicles/${editingId}`, form);
      } else {
        await api.post('/vehicles', form);
      }
      fetchVehicles();
      resetForm();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce vehicule ?')) return;
    try {
      await api.delete(`/vehicles/${id}`);
      fetchVehicles();
    } catch { }
  };

  const startEdit = (v) => {
    setForm({ name: v.name, type: v.type, seats: v.seats, pricePerKm: v.pricePerKm, description: v.description || '', imageUrl: v.imageUrl || '', isActive: v.isActive });
    setEditingId(v.id);
    setShowAdd(true);
  };

  const resetForm = () => {
    setForm({ name: '', type: 'BERLINE', seats: 4, pricePerKm: 1.5, description: '', imageUrl: '', isActive: true });
    setEditingId(null);
    setShowAdd(false);
  };

  const update = (field, value) => setForm(p => ({ ...p, [field]: value }));

  return (
    <div className="pt-20 min-h-screen" data-testid="admin-vehicles-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          <AdminSidebar />
          <div className="flex-1">
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-heading text-3xl text-white">Vehicules</h1>
              <button onClick={() => { resetForm(); setShowAdd(true); }}
                className="flex items-center gap-2 bg-desti-gold text-desti-black font-medium text-sm px-4 py-2 hover:bg-desti-gold-hover transition-colors"
                data-testid="add-vehicle-btn">
                <Plus size={16} /> Ajouter
              </button>
            </div>

            {showAdd && (
              <div className="bg-desti-surface border border-desti-gold/20 p-6 mb-6" data-testid="vehicle-form">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-xl text-white">{editingId ? 'Modifier' : 'Nouveau vehicule'}</h3>
                  <button onClick={resetForm} className="text-white/40 hover:text-white"><X size={18} /></button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Nom</label>
                    <input type="text" value={form.name} onChange={e => update('name', e.target.value)}
                      className="w-full bg-desti-black border-b border-white/20 px-3 py-2 text-white text-sm focus:outline-none focus:border-desti-gold"
                      data-testid="vehicle-name-input" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Type</label>
                    <select value={form.type} onChange={e => update('type', e.target.value)}
                      className="w-full bg-desti-black border-b border-white/20 px-3 py-2 text-white text-sm focus:outline-none focus:border-desti-gold appearance-none"
                      data-testid="vehicle-type-select">
                      {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Places</label>
                    <input type="number" value={form.seats} onChange={e => update('seats', parseInt(e.target.value))}
                      className="w-full bg-desti-black border-b border-white/20 px-3 py-2 text-white text-sm focus:outline-none focus:border-desti-gold"
                      data-testid="vehicle-seats-input" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Prix/km (EUR)</label>
                    <input type="number" step="0.01" value={form.pricePerKm} onChange={e => update('pricePerKm', parseFloat(e.target.value))}
                      className="w-full bg-desti-black border-b border-white/20 px-3 py-2 text-white text-sm focus:outline-none focus:border-desti-gold"
                      data-testid="vehicle-price-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Description</label>
                    <input type="text" value={form.description} onChange={e => update('description', e.target.value)}
                      className="w-full bg-desti-black border-b border-white/20 px-3 py-2 text-white text-sm focus:outline-none focus:border-desti-gold"
                      data-testid="vehicle-desc-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">URL image</label>
                    <input type="text" value={form.imageUrl} onChange={e => update('imageUrl', e.target.value)}
                      className="w-full bg-desti-black border-b border-white/20 px-3 py-2 text-white text-sm focus:outline-none focus:border-desti-gold"
                      data-testid="vehicle-image-input" />
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-xs uppercase tracking-widest text-desti-gold">Actif</label>
                    <button onClick={() => update('isActive', !form.isActive)}
                      className={`w-10 h-5 rounded-full transition-colors ${form.isActive ? 'bg-desti-gold' : 'bg-white/10'}`}
                      data-testid="vehicle-active-toggle">
                      <div className={`w-4 h-4 bg-white rounded-full transition-transform mx-0.5 ${form.isActive ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>
                </div>
                <button onClick={handleSave}
                  className="mt-4 bg-desti-gold text-desti-black font-medium text-sm px-6 py-2 hover:bg-desti-gold-hover transition-colors"
                  data-testid="vehicle-save-btn">
                  {editingId ? 'Mettre a jour' : 'Creer'}
                </button>
              </div>
            )}

            <div className="space-y-3">
              {vehicles.map(v => (
                <div key={v.id} className="bg-desti-surface border border-white/5 p-4 flex items-center gap-4" data-testid={`admin-vehicle-${v.id}`}>
                  {v.imageUrl && <img src={v.imageUrl} alt={v.name} className="w-20 h-14 object-cover shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-white font-medium">{v.name}</p>
                      <span className={`text-xs px-2 py-0.5 ${v.isActive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                        {v.isActive ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                    <p className="text-xs text-desti-text mt-1">{v.type} | {v.seats} places | {v.pricePerKm?.toFixed(2)} EUR/km</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => startEdit(v)} className="p-2 text-white/40 hover:text-desti-gold transition-colors" data-testid={`edit-vehicle-${v.id}`}>
                      <PencilSimple size={16} />
                    </button>
                    <button onClick={() => handleDelete(v.id)} className="p-2 text-white/40 hover:text-red-400 transition-colors" data-testid={`delete-vehicle-${v.id}`}>
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
